import * as React from 'react';
import { t, tryT, type MessageKey } from '@reos/i18n';
import { canSeeMenuItem } from '../lib/capabilities.js';
import { useSession } from '../stores/session.js';
import {
  getCostsSettings,
  updateCostsSettings,
  createCostCategory,
  updateCostCategory,
  type CostCategoryDto,
} from '../lib/costs-api.js';
import {
  clearTenantFlag,
  getTenantFlagOverride,
  setTenantFlag,
  PILOT_HIDDEN_SECTIONS_FLAG_KEY,
  PILOT_MENU_FLAG_KEY,
} from '../lib/flags-api.js';
import { getTenantBrand, updateTenantBrand, type TenantBrand } from '../lib/brand-api.js';
import { getResolveUnknownStats, type ResolveUnknownStats } from '../lib/resolve-unknown-api.js';
import { DomainApiError } from '../lib/api-error.js';
import { readFileAsDataUrl } from '../lib/file-data-url.js';

/**
 * `/admin` (MENU_DEFINITIONS uz na tuto cestu odkazovalo, viz router.tsx) -
 * R5 sem prida jedinou sekci: kategorie nakladu + cost_alert_pct. Jen
 * owner/admin (finance.cost_category.manage) - hlidano API 403 i tady v UI
 * (defense in depth, stejny vzor jako kanban-card canUpdate).
 */
export function AdministracePage(): React.JSX.Element {
  const roles = useSession((s) => s.user?.roles ?? []);
  const allowed = canSeeMenuItem(roles, 'finance.cost_category.manage');

  if (!allowed) {
    return (
      <div className="p-6" data-testid="administrace-page">
        <h1 className="mb-4 text-xl font-bold">{t('webAdmin.administrace.title', { locale: 'cs' })}</h1>
        <p className="text-sm text-gray-500" role="alert">
          {t('webAdmin.administrace.forbidden', { locale: 'cs' })}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="administrace-page">
      <h1 className="mb-4 text-xl font-bold">{t('webAdmin.administrace.title', { locale: 'cs' })}</h1>
      <PilotMenuSection />
      <HiddenSectionsSection />
      <BrandSection />
      <CostSettingsSection />
      <PilotTuningSection />
    </div>
  );
}

/**
 * Krok O5b: brand kancelare pro generovane reporty. Prazdne pole = vychozi
 * vzhled REOS (posila se `null`, ne prazdny retezec — API rozlisuje „smazat"
 * od „nemenit"). Nahled ukazuje barvy tak, jak je dokument pouzije; skutecny
 * report se otevira z detailu zakazky, aby nahled nikdy nepredstiral data.
 */
/** Logo nahrane ze souboru (`data:` URI), ne odkaz na obrazek na webu. */
function isDataUrl(value: string | null | undefined): boolean {
  return value !== null && value !== undefined && value.startsWith('data:');
}

function BrandSection(): React.JSX.Element {
  const [form, setForm] = React.useState<TenantBrand | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      setForm(await getTenantBrand());
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.administrace.brandLoadError', { locale: 'cs' }));
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  function field(key: keyof TenantBrand, value: string): void {
    setSaved(false);
    setForm((current) => (current === null ? current : { ...current, [key]: value }));
  }

  /**
   * Krok FAK4: soubor -> `data:` URI do pole `logoUrl`. Strop 150 kB drzi
   * vysledny retezec pod limitem `safeLogoUrl()` v `@reos/reports` (200 kB) -
   * vetsi logo by se do dokumentu tise nevlozilo.
   */
  const MAX_LOGO_FILE_BYTES = 150_000;

  async function pickLogoFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    if (file === undefined) return;
    setError(null);
    if (file.size > MAX_LOGO_FILE_BYTES) {
      setError(t('webAdmin.administrace.brandLogoFileTooLarge', { locale: 'cs' }));
      return;
    }
    try {
      field('logoUrl', await readFileAsDataUrl(file));
    } catch {
      setError(t('webAdmin.administrace.brandLogoFileError', { locale: 'cs' }));
    }
  }

  async function handleSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (form === null) return;
    setBusy(true);
    setError(null);
    try {
      const orNull = (value: string | null): string | null => (value === null || value.trim() === '' ? null : value.trim());
      const next = await updateTenantBrand({
        logoUrl: orNull(form.logoUrl),
        primaryColor: orNull(form.primaryColor),
        accentColor: orNull(form.accentColor),
        footerText: orNull(form.footerText),
        contactLine: orNull(form.contactLine),
      });
      setForm(next);
      setSaved(true);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.administrace.brandSaveError', { locale: 'cs' }));
    } finally {
      setBusy(false);
    }
  }

  const primary = form?.primaryColor ?? '#1f6b4f';
  const accent = form?.accentColor ?? '#e69a21';

  return (
    <section className="mb-6" data-testid="brand-section">
      <h2 className="mb-2 text-lg font-semibold">{t('webAdmin.administrace.brandHeading', { locale: 'cs' })}</h2>
      <p className="mb-2 text-sm text-gray-600">{t('webAdmin.administrace.brandDescription', { locale: 'cs' })}</p>

      {error !== null && (
        <p role="alert" className="mb-2 text-red-600">
          {error}
        </p>
      )}
      {saved && (
        <p className="mb-2 text-sm text-green-700" data-testid="brand-saved">
          {t('webAdmin.administrace.brandSaved', { locale: 'cs' })}
        </p>
      )}

      {form === null ? (
        <p>{t('webAdmin.administrace.loading', { locale: 'cs' })}</p>
      ) : (
        <form onSubmit={(e) => void handleSave(e)} className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm">
              {t('webAdmin.administrace.brandPrimaryColor', { locale: 'cs' })}
              <input
                className="rh-input block w-32"
                value={form.primaryColor ?? ''}
                placeholder="#1f6b4f"
                onChange={(e) => field('primaryColor', e.target.value)}
                data-testid="brand-primary-color"
              />
            </label>
            <label className="text-sm">
              {t('webAdmin.administrace.brandAccentColor', { locale: 'cs' })}
              <input
                className="rh-input block w-32"
                value={form.accentColor ?? ''}
                placeholder="#e69a21"
                onChange={(e) => field('accentColor', e.target.value)}
                data-testid="brand-accent-color"
              />
            </label>
          </div>
          {/*
            Krok FUNK1 bod 3: logo nahrane ze souboru je `data:` URI o
            desitkach kilobajtu. Do UXR/FAK4 se cely base64 retezec vypisoval
            v poli „URL loga" (nalez z pruchodu 4. 10. 2026) — clovek v nem
            nic neprecte a ani ho tam rucne nenapise. Pole proto ukazuje
            NAHLED a vetu „nahráno ze souboru"; textove pole zustava jen pro
            logo z ADRESY (`http(s)://…`), kde ma smysl.
          */}
          {isDataUrl(form.logoUrl) ? (
            <div className="text-sm" data-testid="brand-logo-uploaded">
              <span className="block">{t('webAdmin.administrace.brandLogoUrl', { locale: 'cs' })}</span>
              <img
                src={form.logoUrl ?? ''}
                alt={t('webAdmin.administrace.brandLogoPreviewAlt', { locale: 'cs' })}
                className="my-1 block rounded border bg-rh-card p-1"
                style={{ maxWidth: 220, maxHeight: 80, objectFit: 'contain' }}
                data-testid="brand-logo-preview"
              />
              <span className="block text-xs text-gray-500">
                {t('webAdmin.administrace.brandLogoFromFile', { locale: 'cs' })}
              </span>
              <button
                type="button"
                className="rh-btn mt-1"
                onClick={() => field('logoUrl', '')}
                data-testid="brand-logo-remove"
              >
                {t('webAdmin.administrace.brandLogoRemove', { locale: 'cs' })}
              </button>
            </div>
          ) : (
            <label className="text-sm">
              {t('webAdmin.administrace.brandLogoUrl', { locale: 'cs' })}
              <input
                className="rh-input block w-full max-w-xl"
                value={form.logoUrl ?? ''}
                onChange={(e) => field('logoUrl', e.target.value)}
                data-testid="brand-logo-url"
              />
            </label>
          )}
          {/*
            Krok FAK4: logo kancelare je `data:` URI (reporty ho vkladaji do
            dokumentu, fakturace z nej bere bajty pro MyUcto - viz
            `logo-image.ts`, ROZHODNUTI FAK4-3). Rucni vypis base64 do
            textoveho pole neni realna cesta, takze soubor prevede prohlizec.
          */}
          <label className="text-sm">
            {t('webAdmin.administrace.brandLogoFile', { locale: 'cs' })}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="block"
              onChange={(e) => void pickLogoFile(e)}
              data-testid="brand-logo-file"
            />
            <span className="block text-xs text-gray-500">{t('webAdmin.administrace.brandLogoFileHint', { locale: 'cs' })}</span>
          </label>
          <label className="text-sm">
            {t('webAdmin.administrace.brandFooterText', { locale: 'cs' })}
            <input
              className="rh-input block w-full max-w-xl"
              value={form.footerText ?? ''}
              onChange={(e) => field('footerText', e.target.value)}
              data-testid="brand-footer-text"
            />
          </label>
          <label className="text-sm">
            {t('webAdmin.administrace.brandContactLine', { locale: 'cs' })}
            <input
              className="rh-input block w-full max-w-xl"
              value={form.contactLine ?? ''}
              onChange={(e) => field('contactLine', e.target.value)}
              data-testid="brand-contact-line"
            />
          </label>
          <div>
            <button type="submit" className="rh-btn rh-btn--primary" disabled={busy} data-testid="brand-save">
              {t('webAdmin.administrace.brandSave', { locale: 'cs' })}
            </button>
          </div>
        </form>
      )}

      <h3 className="mt-4 mb-1 font-medium">{t('webAdmin.administrace.brandPreviewHeading', { locale: 'cs' })}</h3>
      <div
        className="max-w-xl rounded p-3 text-sm"
        style={{ background: primary, color: '#ffffff' }}
        data-testid="brand-preview"
      >
        <div style={{ fontWeight: 700 }}>{form?.officeName ?? ''}</div>
        <div style={{ color: accent }}>{t('webAdmin.administrace.brandPreviewSample', { locale: 'cs' })}</div>
      </div>
      <p className="mt-1 text-xs text-gray-500">{t('webAdmin.administrace.brandPreviewNote', { locale: 'cs' })}</p>
    </section>
  );
}

interface TenantFlagSectionProps {
  readonly flagKey: string;
  readonly heading: string;
  readonly description: string;
  readonly enableLabel: string;
  readonly disableLabel: string;
  readonly statusOn: string;
  readonly statusOff: string;
  readonly loadError: string;
  readonly saveError: string;
  /** Prefix testidů — kazdy flag ma vlastní (jinak by se prepsaly). */
  readonly testId: string;
}

/**
 * Přepínač jednoho flagu per tenant — bez zásahu do DB, jen existující
 * admin API (tenant override PUT/DELETE `/v1/admin/tenants/:id/flags/:key`).
 * Krok O1 (`pilot.menu`) i krok UX1 (`pilot.showHiddenSections`) jdou
 * stejnou cestou, jinak by se pravidlo „flag = jedna sekce v /admin“
 * rozbilo při každém dalším flagu.
 */
function TenantFlagSection(props: TenantFlagSectionProps): React.JSX.Element {
  const { flagKey, testId } = props;
  const tenantId = useSession((s) => s.tenant?.id);
  const [enabled, setEnabled] = React.useState<boolean | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    if (tenantId === undefined) return;
    try {
      const override = await getTenantFlagOverride(tenantId, flagKey);
      setEnabled(override ?? false);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t(props.loadError as MessageKey, { locale: 'cs' }));
    }
  }, [tenantId, flagKey, props.loadError]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function handleToggle(): Promise<void> {
    if (tenantId === undefined) return;
    setBusy(true);
    setError(null);
    try {
      if (enabled === true) {
        await clearTenantFlag(tenantId, flagKey);
      } else {
        await setTenantFlag(tenantId, flagKey, true);
      }
      await load();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t(props.saveError as MessageKey, { locale: 'cs' }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mb-6" data-testid={`${testId}-section`}>
      <h2 className="mb-2 text-lg font-semibold">{props.heading}</h2>
      <p className="mb-2 text-sm text-gray-600">{props.description}</p>

      {error !== null && (
        <p role="alert" className="mb-2 text-red-600">
          {error}
        </p>
      )}

      {enabled === null ? (
        <p>{t('webAdmin.administrace.loading', { locale: 'cs' })}</p>
      ) : (
        <div className="flex items-center gap-2">
          <span data-testid={`${testId}-status`}>{enabled ? props.statusOn : props.statusOff}</span>
          <button
            type="button"
            className="rh-btn rh-btn--primary"
            disabled={busy}
            onClick={() => void handleToggle()}
            data-testid={`${testId}-toggle`}
          >
            {enabled ? props.disableLabel : props.enableLabel}
          </button>
        </div>
      )}
    </section>
  );
}

/** Krok O1: flag `pilot.menu` per tenant. */
function PilotMenuSection(): React.JSX.Element {
  return (
    <TenantFlagSection
      flagKey={PILOT_MENU_FLAG_KEY}
      testId="pilot-menu"
      heading={t('webAdmin.administrace.pilotMenuHeading', { locale: 'cs' })}
      description={t('webAdmin.administrace.pilotMenuDescription', { locale: 'cs' })}
      statusOn={t('webAdmin.administrace.pilotMenuStatusOn', { locale: 'cs' })}
      statusOff={t('webAdmin.administrace.pilotMenuStatusOff', { locale: 'cs' })}
      enableLabel={t('webAdmin.administrace.pilotMenuEnable', { locale: 'cs' })}
      disableLabel={t('webAdmin.administrace.pilotMenuDisable', { locale: 'cs' })}
      loadError="webAdmin.administrace.pilotMenuLoadError"
      saveError="webAdmin.administrace.pilotMenuSaveError"
    />
  );
}

/**
 * Krok UX1 (AUD1 §3.3): skupina „Skrýt do napojení“ — Pozornost, Poptávky,
 * Prohlídky, Schránka, Dokumenty, Zdroje dat, Nastavení… Výchozí stav je
 * SKRYTO (vedou do prázdna); přepínač je odemyká pro daný tenant.
 */
function HiddenSectionsSection(): React.JSX.Element {
  return (
    <TenantFlagSection
      flagKey={PILOT_HIDDEN_SECTIONS_FLAG_KEY}
      testId="hidden-sections"
      heading={t('webAdmin.administrace.hiddenSectionsHeading', { locale: 'cs' })}
      description={t('webAdmin.administrace.hiddenSectionsDescription', { locale: 'cs' })}
      statusOn={t('webAdmin.administrace.hiddenSectionsStatusOn', { locale: 'cs' })}
      statusOff={t('webAdmin.administrace.hiddenSectionsStatusOff', { locale: 'cs' })}
      enableLabel={t('webAdmin.administrace.hiddenSectionsEnable', { locale: 'cs' })}
      disableLabel={t('webAdmin.administrace.hiddenSectionsDisable', { locale: 'cs' })}
      loadError="webAdmin.administrace.hiddenSectionsLoadError"
      saveError="webAdmin.administrace.hiddenSectionsSaveError"
    />
  );
}

function CostSettingsSection(): React.JSX.Element {
  const [alertPct, setAlertPct] = React.useState('30');
  const [categories, setCategories] = React.useState<CostCategoryDto[] | null>(null);
  const [newCategory, setNewCategory] = React.useState({ code: '', name: '' });
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const settings = await getCostsSettings();
      setAlertPct(String(settings.alertPct));
      setCategories(settings.categories);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : 'Načtení nastavení nákladů selhalo.');
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function handleSaveAlertPct(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const pct = Number(alertPct);
    if (!Number.isFinite(pct)) return;
    setBusy(true);
    setError(null);
    try {
      const settings = await updateCostsSettings(pct);
      setAlertPct(String(settings.alertPct));
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : 'Uložení alert % selhalo.');
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleActive(category: CostCategoryDto): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updateCostCategory(category.id, { active: !category.active });
      await load();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : 'Uložení kategorie selhalo.');
    } finally {
      setBusy(false);
    }
  }

  async function handleAddCategory(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (newCategory.code === '' || newCategory.name === '') return;
    setBusy(true);
    setError(null);
    try {
      await createCostCategory(newCategory.code, newCategory.name);
      setNewCategory({ code: '', name: '' });
      await load();
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : 'Přidání kategorie selhalo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="cost-settings-section">
      <h2 className="mb-2 text-lg font-semibold">{t('webAdmin.administrace.costsHeading', { locale: 'cs' })}</h2>

      {error !== null && (
        <p role="alert" className="mb-2 text-red-600">
          {error}
        </p>
      )}

      <form onSubmit={(e) => void handleSaveAlertPct(e)} className="mb-4 flex items-end gap-2">
        <label className="text-sm">
          {t('webAdmin.administrace.thresholdLabel', { locale: 'cs' })}
          <input
            type="number"
            min={1}
            max={500}
            className="rh-input block w-24"
            value={alertPct}
            onChange={(e) => setAlertPct(e.target.value)}
            aria-label={t('webAdmin.administrace.thresholdAriaLabel', { locale: 'cs' })}
            data-testid="cost-alert-pct-input"
          />
        </label>
        <button type="submit" className="rh-btn rh-btn--primary" disabled={busy}>
          {t('webAdmin.administrace.save', { locale: 'cs' })}
        </button>
      </form>

      <h3 className="mb-1 font-medium">{t('webAdmin.administrace.categoriesHeading', { locale: 'cs' })}</h3>
      {categories === null ? (
        <p>{t('webAdmin.administrace.loading', { locale: 'cs' })}</p>
      ) : (
        <ul className="mb-4 space-y-1 text-sm" data-testid="cost-categories-list">
          {categories.map((cat) => (
            <li key={cat.id} className="flex items-center gap-2" data-testid={`cost-category-${cat.id}`}>
              <span className={cat.active ? '' : 'text-gray-400 line-through'}>{cat.name}</span>
              <button type="button" className="rh-btn" disabled={busy} onClick={() => void handleToggleActive(cat)}>
                {cat.active ? 'Deaktivovat' : 'Aktivovat'}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={(e) => void handleAddCategory(e)} className="flex items-end gap-2">
        <label className="text-sm">
          {t('webAdmin.administrace.code', { locale: 'cs' })}
          <input
            className="rh-input block"
            value={newCategory.code}
            onChange={(e) => setNewCategory((f) => ({ ...f, code: e.target.value }))}
            aria-label={t('webAdmin.administrace.newCodeAriaLabel', { locale: 'cs' })}
          />
        </label>
        <label className="text-sm">
          {t('webAdmin.administrace.name', { locale: 'cs' })}
          <input
            className="rh-input block"
            value={newCategory.name}
            onChange={(e) => setNewCategory((f) => ({ ...f, name: e.target.value }))}
            aria-label={t('webAdmin.administrace.newNameAriaLabel', { locale: 'cs' })}
          />
        </label>
        <button type="submit" className="rh-btn rh-btn--primary" disabled={busy} data-testid="cost-category-add">
          {t('webAdmin.administrace.addCategory', { locale: 'cs' })}
        </button>
      </form>
    </section>
  );
}

/**
 * Nacteni statistik fronty „K doplnění" pro sekci „Ladění pilotu".
 * Chyba NENI fatalni — sekce se proste nevykresli a zbytek administrace stoji.
 */
function PilotTuningSection(): React.JSX.Element | null {
  const [stats, setStats] = React.useState<ResolveUnknownStats | null>(null);

  React.useEffect(() => {
    let alive = true;
    void getResolveUnknownStats().then(
      (result) => { if (alive) setStats(result); },
      () => undefined,
    );
    return () => { alive = false; };
  }, []);

  if (stats === null) return null;
  return <StatsPanel stats={stats} />;
}

/**
 * „Ladění pilotu" — z cehoz se vybira, co automatizovat dal (bod 4 zadani H1).
 *
 * Krok FUNK1 bod 3: PRESUNUTO Z `/k-doplneni`. Nalez R.B. 4. 10. 2026:
 * makler videl ve FRONTE blok se STROJOVYMI nazvy zdroju (`ares`,
 * `cuzk.dmrSg…`), pocty behu a medianem doby reseni. Je to interni
 * diagnostika pilotu, ne prace maklere — proto stoji tady, na obrazovce,
 * kterou otevira jen spravce kancelare (cela `/admin` je za
 * `finance.cost_category.manage`, viz `AdministracePage`).
 */
/**
 * Krok UXP3b bod 1 (nalez: `/admin` ukazoval strojove klice registru zdroju
 * jako „cuzk.wsdp.sbirka_listin" / „dtm.dmvs.spravci").
 *
 * SSOT lidskeho nazvu zdroje je `dd.report.source.<id>` — ten se pta jako
 * prvni (stejnou cestou jako report v5, `report/v5/segments/flat.ts`).
 * ODLISNOST (pravidlo 2 CLAUDE.md §8): do nej NEJDE doplnit chybejici id.
 * Pritomnost klice v tom katalogu je v reportu zaroven ROZHODNUTIM, ze se
 * zdroj klientovi vypise (`sourceNames()` v `report-v5/flat/documents.ts`
 * filtruje podle existence labelu). Doplneni `cuzk.wsdp.sbirka_listin` a
 * `dtm.dmvs.spravci` proto pridalo radky do hotoveho klientskeho reportu
 * (cervena `pnpm test:golden`, oprava 1 tohoto kroku). Interni sluzby
 * registru, ktere v reportu jako samostatny zdroj stat nemaji, maji nazev
 * v provoznim katalogu `webAdmin.resolveUnknown.stats.sourceName.<id>`;
 * mnoziny jsou disjunktni, zadny nazev neni ve dvou mistech.
 *
 * Kdyz klic (zdroj, otazka, duvod) neni ani v jednom, zustava SCHOVANY
 * v `<details>` — vzor „Pro správce" z `audit.tsx`, ktery hlidac
 * technickeho textu vynecha (uzavreny `<details>` nema viditelny obsah
 * krome `<summary>`).
 */
function polozkaLabel(key: string): string | null {
  return (
    tryT(`dd.report.source.${key}` as MessageKey, { locale: 'cs' }) ??
    tryT(`webAdmin.resolveUnknown.stats.sourceName.${key}` as MessageKey, { locale: 'cs' }) ??
    null
  );
}

function StatsPanel({ stats }: { stats: ResolveUnknownStats }): React.JSX.Element {
  const rows: readonly {
    readonly caption: string;
    readonly items: ResolveUnknownStats['bySource'];
  }[] = [
    { caption: t('webAdmin.resolveUnknown.stats.bySource', { locale: 'cs' }), items: stats.bySource },
    { caption: t('webAdmin.resolveUnknown.stats.byQuestion', { locale: 'cs' }), items: stats.byQuestion },
    { caption: t('webAdmin.resolveUnknown.stats.byReason', { locale: 'cs' }), items: stats.byReasonCode },
  ];
  return (
    <section className="mt-6 border-t pt-4" data-testid="resolve-unknown-stats">
      <h2 className="mb-2 text-base font-semibold">
        {t('webAdmin.resolveUnknown.stats.heading', { locale: 'cs' })}
      </h2>
      <ul className="text-sm">
        <li>
          {t('webAdmin.resolveUnknown.stats.tasksTotal', {
            locale: 'cs',
            values: { count: String(stats.tasksTotal) },
          })}
        </li>
        <li>
          {t('webAdmin.resolveUnknown.stats.tasksOpen', {
            locale: 'cs',
            values: { count: String(stats.tasksOpen) },
          })}
        </li>
        <li data-testid="resolve-unknown-stats-per-run">
          {t('webAdmin.resolveUnknown.stats.perRun', {
            locale: 'cs',
            values: {
              avg: String(stats.tasksPerRunAvg ?? 0),
              max: String(stats.tasksPerRunMax ?? 0),
              runs: String(stats.runsWithTasks),
            },
          })}
        </li>
        <li data-testid="resolve-unknown-stats-median">
          {t('webAdmin.resolveUnknown.stats.median', {
            locale: 'cs',
            values: { minutes: String(stats.medianResolutionMinutes ?? 0) },
          })}
        </li>
      </ul>
      {rows.map((row) => (
        <div key={row.caption} className="mt-3 text-sm">
          <h3 className="font-medium">{row.caption}</h3>
          {row.items.length === 0 ? (
            <p className="text-xs text-gray-500">{t('webAdmin.resolveUnknown.stats.none', { locale: 'cs' })}</p>
          ) : (
            <ul className="text-xs text-gray-700">
              {row.items.map((item) => {
                const counts = t('webAdmin.resolveUnknown.stats.countPair', {
                  locale: 'cs',
                  values: { open: String(item.open), resolved: String(item.resolved) },
                });
                const label = polozkaLabel(item.key);
                return (
                  <li key={item.key}>
                    {label !== null ? (
                      <>
                        {label} — {counts}
                      </>
                    ) : (
                      <details>
                        <summary className="cursor-pointer">
                          {t('webAdmin.resolveUnknown.stats.unknownItem', { locale: 'cs' })} — {counts}
                        </summary>
                        <p className="mt-1 text-gray-400">
                          {t('webAdmin.resolveUnknown.stats.rawKeyLabel', {
                            locale: 'cs',
                            values: { key: item.key },
                          })}
                        </p>
                      </details>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ))}
    </section>
  );
}
